import { type Chat, type ChatSearch } from '@src/api/shared/community/chatApiClient';
import DefaultAvatar from '@src/assets/images/avatar/default.png';
import ChevronCircleRightIcon from '@src/assets/images/common/icon/chevron-circle-right-thin/ChevronCircleRightIcon';
import ChatAttachmentList from '@src/features/shared/community/components/chat/chatList/ChatAttachmentList';
import useIsMobile from '@src/hooks/useIsMobile';
import { Attachment } from '@src/idb';
import { highlightText, highlightTextAsHTML } from '@src/utils/textHighlight';
import dayjs from 'dayjs';
import React from 'react';

interface SearchResultCardProps {
  result: ChatSearch;
  keyword?: string;
  onResultClick?: (result: ChatSearch) => void;
}

function SearchResultCard({ result, keyword = '', onResultClick }: SearchResultCardProps) {
  const isMobileDevice = useIsMobile();

  const handleDesktopClick = (e: React.MouseEvent) => {
    if (isMobileDevice) {
      e.stopPropagation();
      return;
    }

    if (onResultClick) {
      onResultClick(result);
    }
  };

  const handleMobileIconClick = (e: React.MouseEvent) => {
    if (!isMobileDevice) {
      e.stopPropagation();
      return;
    }

    if (onResultClick) {
      onResultClick(result);
    }
  };

  const handleAttachmentAreaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleAttachmentAreaTouch = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  const adaptedAttachments: Attachment[] = (result.attachments || []).map((attachment) => ({
    name: attachment.file_name,
    type: attachment.extension || '',
    size: 0,
    blob: new Blob(),
    chatAttachment: attachment,
  }));

  const searchChat: Chat = {
    id: result.id,
    message: result.message,
    is_edited: result.is_edited === 1,
    created_at: result.created_at,
    updated_at: result.updated_at,
    date_label: result.date_label,
    user_type: result.user.user_type!,
    user_id: result.user.user_id,
    can_click: false,
    channel_member_id: 0,
    attachments: result.attachments,
    display_name: result.user.display_name,
    avatar_url: result.user.avatar_url,
  };

  return (
    <div
      className='cursor-pointer select-none rounded-[4px] border border-gray-200 bg-white px-6 pb-4 pt-5 transition-colors'
      onClick={handleDesktopClick}
    >
      <div className='flex flex-col gap-3'>
        <div className='flex items-center gap-3'>
          <img
            className='size-8 rounded-full'
            src={result.user.avatar_url || DefaultAvatar}
            alt={result.user.display_name || 'User'}
          />
          <div className='flex w-full min-w-0 items-center gap-2'>
            <div className='flex min-w-0 items-center gap-2'>
              <span className='block min-w-0 truncate text-sm font-medium'>
                {highlightText(result.user.display_name || '', keyword)}
              </span>
              <div className='flex shrink-0 items-center gap-1 whitespace-nowrap'>
                <span className='text-[10px]'>{result.date_label}</span>
                <span className='text-[10px]'>{dayjs(result.created_at).format('HH:mm')}</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleMobileIconClick}
            className='relative z-40 hidden md:block'
            type='button'
          >
            <ChevronCircleRightIcon
              className='shrink-0'
              size={20}
              backgroundColor='#5B8CEC'
              strokeColor='white'
            />
          </button>
        </div>
        <div className='flex flex-1 flex-col gap-2 pl-[40px]'>
          <div
            className='tiptap-content line-clamp-5 text-sm text-gray-700'
            dangerouslySetInnerHTML={{ __html: highlightTextAsHTML(result.message, keyword) }}
          />
          {result.attachments && result.attachments.length > 0 && (
            <div
              className='select-none'
              onClick={handleAttachmentAreaClick}
              onTouchStart={handleAttachmentAreaTouch}
              onTouchMove={handleAttachmentAreaTouch}
              onTouchEnd={handleAttachmentAreaTouch}
            >
              <ChatAttachmentList
                className='!mt-2'
                attachments={adaptedAttachments}
                chat={searchChat}
                hasMessageContent={Boolean(result.message)}
                allowImagePreview={false}
                keyword={keyword}
                includeImagesInFiles={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchResultCard;
